import { ReadonlyVec3, vec3 } from 'gl-matrix';

export enum ContourGeometricType {
    POINT,
    OPEN_PLANAR,
    OPEN_NONPLANAR,
    CLOSED_PLANAR,
}

/**
 * {@link Contour} represents a single enclosed sequence of 3D points that are connected with lines.
 */
export class Contour implements Iterable<ReadonlyVec3> {
    /**
     * Array of {@link vec3 vectices} that make up this {@link Contour}.\
     * All of these {@link vec3 points} are unique.\
     * So the first point is not duplicated.
     */
    private readonly _vertices: vec3[];

    /**
     * The centroid of this {@link Contour}.\
     * This is the average of all the vertices stored within this {@link Contour}.\
     * Saved into a parameter as it takes time to compute and only changes when the {@link vertices} change.
     */
    private _centroid?: vec3;

    public constructor(vertices: vec3[]) {
        if (vec3.equals(vertices[0], vertices[vertices.length - 1])) {
            vertices.pop();
        }
        this._vertices = vertices;
    }

    public getVertex(index: number): ReadonlyVec3 {
        return this._vertices[index];
    }

    public get vertices(): ReadonlyArray<ReadonlyVec3> {
        return this._vertices;
    }

    /**
     * Method to get the number of {@link vertices} within this {@link Contour}.
     */
    public get vertexCount(): number {
        return this._vertices.length;
    }

    /**
     * Method to get the number of edges within this {@link Contour}.\
     * As the first point should be connected to the last point, this is equal to {@link vertexCount}.
     */
    public get edgeCount(): number {
        return this._vertices.length;
    }

    /**
     * Method to get the {@link _centroid centroid} of this {@link Contour}.\
     * The {@link _centroid centroid} is recomputed if it's not yet available.
     */
    public get centroid(): ReadonlyVec3 {
        if (!this._centroid) {
            this._centroid = vec3.create();
            for (const vertex of this._vertices) {
                vec3.add(this._centroid, this._centroid, vertex);
            }
            this._centroid = vec3.scale(
                this._centroid,
                this._centroid,
                1 / this.vertexCount
            );
        }
        return this._centroid;
    }

    [Symbol.iterator](): Iterator<ReadonlyVec3> {
        return this._vertices[Symbol.iterator]();
    }
}
